 
 
/**
 * Source https://github.com/GistApps/deep-serialize-form/blob/main/src/deep-serialize-form.js
 * - Includes augmentations denoted by ℹ️
 * - If multiple `HTMLInputElement`s have the same name we'll output an array,
 *   except when all values are boolean (i.e. styled checkboxes)
 */
export function deepSerializeForm(
    form: HTMLFormElement
): Record<string, string | number | boolean | Record<string, any>> {
    var obj = {};
    var coerce_types = { true: !0, false: !1, null: null };

    const formDataEntries = formDataEntriesPolyfill(form);

    for (var pair of formDataEntries) {
        var key = pair[0];
        var val = pair[1] as string | number;
        var cur = obj as any;
        var i = 0;
        var keys = key.split('][');
        var keys_last = keys.length - 1;

        // ℹ️ Fix date format for backend
        if (key.includes('date') && typeof val === 'string' && val.split('-').length === 3) {
            // current format from date picker: YYYY-MM-DD
            const [year, month, day] = val.split('-');
            // expected format for backend: DD/MM/YYYY
            val = `${day}/${month}/${year}`;
        }

        if (/\[/.test(keys[0]) && /\]$/.test(keys[keys_last])) {
            keys[keys_last] = keys[keys_last].replace(/\]$/, '');

            keys = keys.shift()!.split('[').concat(keys);

            keys_last = keys.length - 1;
        } else {
            keys_last = 0;
        }

        val =
            val && typeof val === 'number'
                ? +val // number
                : val === 'undefined'
                ? undefined // undefined
                : coerce_types[val] !== undefined
                ? coerce_types[val] // true, false, null
                : val;

        if (keys_last) {
            for (; i <= keys_last; i++) {
                key = keys[i] === '' ? cur.length : keys[i];
                cur = cur[key] =
                    i < keys_last
                        ? cur[key] || (keys[i + 1] && isNaN(keys[i + 1] as any) ? {} : [])
                        : val;
            }
        } else {
            if (Array.isArray(obj[key])) {
                obj[key].push(val);
            } else if (obj[key] !== undefined) {
                obj[key] = [obj[key], val];
            } else {
                obj[key] = val;
            }
        }
    }

    // ℹ️ given array of bools (checkbox), choose last
    for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value) && value.length && value.every((x) => typeof x === 'boolean')) {
            obj[key] = value[value.length - 1];
        }
    }

    return obj;
}

/**
 * FormData API unsupported by old Safari https://caniuse.com/mdn-api_formdata
 * Polyfill source https://github.com/jimmywarting/FormData/blob/master/FormData.js
 */
function formDataEntriesPolyfill(form: HTMLFormElement) {
    const output = [] as [string, FormDataEntryValue][];
    const shouldByPassWebsiteLinkPayment = form.shouldByPassWebsiteLinkPayment?.value;

    (Array.from(form.elements) as HTMLInputElement[]).forEach((elm) => {
        if (
            !elm.name ||
            elm.disabled ||
            elm.type === 'submit' ||
            elm.type === 'button' ||
            elm.matches('form fieldset[disabled] *')
        )
            return;

        if (elm.type === 'file') {
            const files =
                elm.files && elm.files.length
                    ? Array.from(elm.files)
                    : [new File([], '', { type: 'application/octet-stream' })]; // #78

            files.forEach((file) => {
                output.push([elm.name, file]);
            });
        } else if (elm.type === 'select-multiple' || elm.type === 'select-one') {
            Array.from((elm as any as HTMLSelectElement).options).forEach(
                (opt) => !opt.disabled && opt.selected && output.push([elm.name, opt.value])
            );
        } else if (elm.type === 'checkbox' || elm.type === 'radio') {
            if (shouldByPassWebsiteLinkPayment === 'true') {
                if (elm.name !== 'features[WEBSITE_URL][selected]' && elm.checked)
                    output.push([elm.name, elm.value]);
            } else {
                if (elm.checked) output.push([elm.name, elm.value]);
            }
        } else {
            const value = elm.type === 'textarea' ? normalizeLinefeeds(elm.value) : elm.value;
            output.push([elm.name, value]);
        }
    });
    return output;
}

type FormDataEntryValue = string | File;

function normalizeLinefeeds(value) {
    return value.replace(/\r?\n|\r/g, '\r\n');
}
